import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import SelectSearchInput from "../SelectSearch";
import Navbar from "../Navbar";
import Footer from "../Footer";
import { FiCheck, FiX } from "react-icons/fi";
import { MdSync } from "react-icons/md";
import { toast } from "react-toastify";
import Select from 'react-select'

function MenuMaster() {
  const [menuData, setMenuData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [token, setToken] = useState("");
  const [selectedOutlet, setSelectedOutlet] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const navigate = useNavigate();
  const Url = process.env.REACT_APP_BASE_URL;

  const allMenuItems = useMemo(() => {
    const flattenItems = (items) => {
      let result = [];
      items.forEach(item => {
        if (item.items) {
          result = [...result, ...flattenItems(item.items)];
        } else {
          result.push(item);
        }
      });
      return result;
    };

    return flattenItems(menuData);
  }, [menuData]);

  const filteredItems = useMemo(() => {
    if (!searchInput) return menuData;

    const searchTerm = searchInput.toLowerCase();
    return allMenuItems.filter(item =>
      item.Name.toLowerCase().includes(searchTerm) ||
      (item.Description && item.Description.toLowerCase().includes(searchTerm))
    );
  }, [searchInput, menuData, allMenuItems]);

  const toggleDropdown = () => {
    setIsMenuOpen(prev => !prev);
  };

  const handleNavigate = (path) => {
    navigate(path);
    setIsMenuOpen(false);
  };

  useEffect(() => {
    const tokenCheck = sessionStorage.getItem("token");
    if (!tokenCheck) {
      navigate("/");
    } else {
      setToken(tokenCheck);
    }
  }, [navigate]);

  useEffect(() => {
    if (token && selectedOutlet) {
      fetchMenu();
    }

    if (searchParams.get("outletName") !== selectedOutlet) {
      setSearchParams({ outletName: selectedOutlet });
    }
  }, [selectedOutlet, token, setSearchParams, Url, searchParams]);

  const fetchMenu = async () => {
    if (!token || !selectedOutlet) return;

    setLoading(true);
    await axios
      .post(`${Url}/item-lists`, {
        token: token,
        outlet: selectedOutlet,
      })
      .then((response) => {
        setMenuData(response.data);
        setLoading(false);
      })
      .catch((err) => {
        setError("Failed to fetch menu data");
        setLoading(false);
        console.log(err);
      });
  }

  const handleSync = async () => {
    try {
      await axios.post(`${Url}/syncmenu`, {
        token: token,
        outlet: selectedOutlet
      })
      toast.success("Menu synced successfully");
      fetchMenu();
    } catch (error) {
      toast.error("Failed to sync menu data");
      console.log("Sync error", error);
    }
  }

  const renderMenuItems = (items) => (
    <table className="min-w-full table-auto bg-white rounded-lg shadow-lg">
      <thead className="bg-gray-200">
        <tr>
          <th className="px-3 py-2 text-left text-gray-600">Name</th>
          <th className="px-3 py-2 text-left text-gray-600">Price</th>
          <th className="px-3 py-2 text-left text-gray-600">State</th>
          <th className="px-3 py-2 text-left text-gray-600">
            Discount Exempt
          </th>
          <th className="px-3 py-2 text-gray-600"></th>
        </tr>
      </thead>
      <tbody>
        {items.map((item, itemIndex) => (
          <tr key={itemIndex} className="border-b">
            <td className="px-3 py-2 text-gray-700 text-left">
              <button
                className="text-blue-600 text-left"
                onClick={() => {
                  sessionStorage.setItem("outlet", selectedOutlet);
                  sessionStorage.setItem("group", item.Description);
                  sessionStorage.setItem("item", item.Name);
                  sessionStorage.setItem("type", item.Type);
                  navigate("/itemdata");
                }}>
                {item.Name}
              </button>
            </td>
            <td className="px-3 py-2 text-gray-700">Rs.{item.Price}</td>
            <td className="px-3 py-2 text-gray-700">{item.state}</td>
            <td className="px-3 py-2 text-gray-700">{item.discountExempt}</td>
            <td className="text-center px-3 py-2">
              {item.hasRecipe ? (
                <FiCheck className="text-green-500 text-xl inline" />
              ) : (
                <FiX className="text-red-500 text-xl inline" />
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const searchOptions = allMenuItems.map(item => ({
    value: item.Name,
    label: `${item.Name} (${item.Description || 'No description'}) - Rs.${item.Price}`,
    data: item
  }));

  const handleSearchChange = (selectedOption) => {
    setSearchInput(selectedOption ? selectedOption.value : "");
  };

  const foodItems = menuData.filter((item) => item.Type === "Food");
  const beverageItems = menuData.filter((item) => item.Type === "Beverage");
  const otherItems = menuData.filter((item) => item.Type === "Other");

  // const deleteAllMenu = async () => {
  //   const isConfirmed = window.confirm("Are you sure you want to delete all menu items? This action cannot be undone.");

  //   if (!isConfirmed) {
  //     return; //
  //   }

  //   try {
  //     await axios.post(`${Url}/deleteallmenu`, { token, outlet: selectedOutlet });
  //     setMenuData([]);
  //   } catch (error) {
  //     console.error("Error deleting menu items:", error);
  //   }
  // };

  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      <div className="p-6 overflow-x-auto overflow-y-auto flex-grow">
        <div className="flex mb-8 px-20 items-center">
          <div className="relative flex-1 flex justify-start">
            <button
              onClick={handleSync}
              className="border-2 border-slate-300 hover:bg-slate-100 bg-slate-700 text-zinc-100 hover:text-zinc-700 rounded-lg flex items-center justify-center px-3 py-2 text-sm"
            >
              <MdSync className="h-5 w-5 mr-2" /> Sync Menu
            </button>
          </div>

          <div className="relative flex-1 flex justify-center text-center">
            <SelectSearchInput
              token={token}
              setToken={setToken}
              setSelectedOutlet={setSelectedOutlet}
              selectedOutlet={selectedOutlet}
            />
          </div>

          <div className="relative flex-1 flex justify-end">
            <div
              className={`cursor-pointer ${isMenuOpen ? 'text-purple-600 underline' : 'text-black'} font-semibold hover:!text-purple-600`}
              onClick={toggleDropdown}
            >
              {isMenuOpen ? 'Recipe Page Options ↓' : 'Recipe Page?'}
            </div>

            {isMenuOpen && (
              <div className="absolute mt-4 bg-white border border-gray-300 rounded-lg shadow-lg z-10 w-44">
                <button
                  className="w-full text-left px-3 py-2 text-sm hover:bg-slate-100"
                  onClick={() => handleNavigate('/additemunit')}
                >
                  Add Unit Definitions
                </button>
                <button
                  className="w-full text-left px-3 py-2 text-sm hover:bg-slate-100"
                  onClick={() => handleNavigate('/viewunitdefinitions')}
                >
                  View Unit Definitions
                </button>
                <button
                  className="w-full text-left px-3 py-2 text-sm hover:bg-slate-100"
                  onClick={() => handleNavigate('/subrecipecost')}
                >
                  Add Sub Recipes
                </button>
                <button
                  className="w-full text-left px-3 py-2 text-sm hover:bg-slate-100"
                  onClick={() => handleNavigate('/viewsubrecipes')}
                >
                  View Sub Recipes
                </button>
                <button
                  className="w-full text-left px-3 py-2 text-sm hover:bg-slate-100"
                  onClick={() => handleNavigate('/menuengineering')}
                >
                  Menu Engineering
                </button>
                <button
                  className="w-full text-left px-3 py-2 text-sm hover:bg-slate-100"
                  onClick={() => handleNavigate('/inventorycalculation')}
                >
                  Inventory Calculation
                </button>
                <button
                  className="w-full text-left px-3 py-2 text-sm hover:bg-slate-100"
                  onClick={() => handleNavigate('/inventoryhistory')}
                >
                  Inventory History
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="max-w-md mx-auto mb-5">
          <Select
            options={searchOptions}
            onChange={handleSearchChange}
            isClearable
            placeholder="Search menu items..."
            className="basic-single"
            classNamePrefix="select"
          />
        </div>

        {/* <div className="flex justify-start gap-5 pb-6">
          <button
            onClick={() => navigate("/fileupload")}
            className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700"
          >
            Upload Menu Data
          </button>

          <button
            onClick={() => navigate("/editmenudata")}
            className="bg-yellow-500 text-white px-6 py-3 rounded-lg hover:bg-yellow-600"
          >
            Edit Menu Data
          </button>

          <button
            onClick={deleteAllMenu}
            className="bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700"
          >
            Delete All
          </button>
        </div> */}
        <div>

          {loading ? (
            <div className="text-center py-4">
              <svg className="animate-spin h-5 w-5 mr-2 inline-block text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
              </svg>
              Loading data...
            </div>
          ) : error ? (
            <div className="text-red-600 bg-red-50 p-4 text-lg font-semibold max-w-xl mx-auto">
              {error}
            </div>
          ) : menuData.length === 0 ? (
            <p className="text-center text-xl text-red-500">No menu available</p>
          ) : searchInput ? (
            <div className="bg-white rounded-lg shadow-lg p-4 max-w-5xl mx-auto">
              <h2 className="text-xl font-semibold text-gray-700 mb-4">Search Results</h2>
              {filteredItems.length > 0 ? (
                <table className="min-w-full table-auto border text-center">
                  <thead className="bg-gray-200">
                    <tr>
                      <th className="px-3 py-2 text-gray-600">Name</th>
                      <th className="px-3 py-2 text-gray-600">Price</th>
                      <th className="px-3 py-2 text-gray-600">Category</th>
                      <th className="px-3 py-2 text-gray-600">Type</th>
                      <th className="px-3 py-2 text-gray-600">Status</th>
                      <th className="px-3 py-2 text-gray-600">Discount Exempt</th>
                      <th className="px-3 py-2 text-gray-600">Recipe</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.map((item, index) => (
                      <tr key={index} className="border-b">
                        <td className="px-3 py-2 text-gray-700">
                          <button
                            className="text-blue-600"
                            onClick={() => {
                              sessionStorage.setItem("outlet", selectedOutlet);
                              sessionStorage.setItem("group", item.Description);
                              sessionStorage.setItem("item", item.Name);
                              sessionStorage.setItem("type", item.Type);
                              navigate("/itemdata");
                            }}>
                            {item.Name}
                          </button>
                        </td>
                        <td className="px-3 py-2 text-gray-700">Rs.{item.Price}</td>
                        <td className="px-3 py-2 text-gray-700">{item.Description}</td>
                        <td className="px-3 py-2 text-gray-700">{item.Type}</td>
                        <td className="px-3 py-2 text-gray-700">{item.state}</td>
                        <td className="px-3 py-2 text-gray-700">{item.discountExempt}</td>
                        <td className="text-center px-3 py-2">
                          {item.hasRecipe ? (
                            <FiCheck className="text-green-500 text-xl inline" />
                          ) : (
                            <FiX className="text-red-500 text-xl inline" />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-center text-gray-500">No items found matching your search</p>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <h2 className="text-2xl font-semibold text-gray-700">Food</h2>
                {foodItems.length > 0 ? (
                  foodItems.map((category, index) => (
                    <div key={index} className="mb-6">
                      <h3 className="text-2xl font-semibold text-gray-600 mb-4">
                        {category.Description}
                      </h3>
                      {category.items &&
                        category.items.length > 0 &&
                        category.items.map((subCategory, subIndex) => (
                          <div key={subIndex} className="mb-4">
                            <h4 className="text-xl font-semibold text-gray-600 mb-2">
                              {subCategory.Description}
                            </h4>
                            {subCategory.items &&
                              subCategory.items.length > 0 &&
                              renderMenuItems(subCategory.items)}
                          </div>
                        ))}
                    </div>
                  ))
                ) : (
                  <p>No food items available</p>
                )}
              </div>

              <div>
                <h2 className="text-2xl font-semibold text-gray-700">Beverage</h2>
                {beverageItems.length > 0 ? (
                  beverageItems.map((category, index) => (
                    <div key={index} className="mb-6">
                      <h3 className="text-2xl font-semibold text-gray-600 mb-4">
                        {category.Description}
                      </h3>
                      {category.items &&
                        category.items.length > 0 &&
                        category.items.map((subCategory, subIndex) => (
                          <div key={subIndex} className="mb-4">
                            <h4 className="text-xl font-semibold text-gray-600 mb-2">
                              {subCategory.Description}
                            </h4>
                            {subCategory.items &&
                              subCategory.items.length > 0 &&
                              renderMenuItems(subCategory.items)}
                          </div>
                        ))}
                    </div>
                  ))
                ) : (
                  <p>No beverage items available</p>
                )}
              </div>

              <div>
                <h2 className="text-2xl font-semibold text-gray-700">Others</h2>
                {otherItems.length > 0 ? (
                  otherItems.map((category, index) => (
                    <div key={index} className="mb-6">
                      <h3 className="text-2xl font-semibold text-gray-600 mb-4">
                        {category.Description}
                      </h3>
                      {category.items &&
                        category.items.length > 0 &&
                        category.items.map((subCategory, subIndex) => (
                          <div key={subIndex} className="mb-4">
                            <h4 className="text-xl font-semibold text-gray-600 mb-2">
                              {subCategory.Description}
                            </h4>
                            {subCategory.items &&
                              subCategory.items.length > 0 &&
                              renderMenuItems(subCategory.items)}
                          </div>
                        ))}
                    </div>
                  ))
                ) : (
                  <p>No other items available</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default MenuMaster;
